import { useState } from "react";
import { validateEmail, validatePhone, validateGSTIN, validatePincode } from "../../utils/formValidation";

export default function ClientForm({ form, setForm, errors = {}, touched = {}, validateField, setTouched }) {
  // If validation functions not provided, create local state
  const [localErrors, setLocalErrors] = useState({});
  const [localTouched, setLocalTouched] = useState({});

  const currentErrors = errors || localErrors;
  const currentTouched = touched || localTouched;

  // Validate field on blur
  const handleValidateField = (name, value) => {
    if (validateField) {
      return validateField(name, value);
    }

    // Local validation if no parent validation provided
    let error = null;

    switch (name) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Client name is required';
        }
        break;

      case 'email':
        if (value && value.trim() !== '') {
          const validation = validateEmail(value);
          error = validation.error;
        }
        break;

      case 'phone':
        if (value && value.trim() !== '') {
          const validation = validatePhone(value);
          error = validation.error;
        }
        break;

      case 'gstin':
        if (value && value.trim() !== '') {
          const validation = validateGSTIN(value);
          error = validation.error;
        }
        break;

      case 'pincode':
        if (value && value.trim() !== '') {
          const validation = validatePincode(value);
          error = validation.error;
        }
        break;

      default:
        break;
    }

    setLocalErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return !error;
  };

  const handleBlur = (name) => {
    if (setTouched) {
      setTouched(prev => ({ ...prev, [name]: true }));
    } else {
      setLocalTouched(prev => ({ ...prev, [name]: true }));
    }
    handleValidateField(name, form[name]);
  };

  const handleChange = (name, value) => {
    setForm({ ...form, [name]: value });
    
    // Clear error when user starts typing
    if (currentErrors[name]) {
      if (validateField) {
        // Parent manages errors
        validateField(name, ''); // Clear error
      } else {
        setLocalErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  const getFieldClassName = (fieldName) => {
    const baseClass = "input";
    
    if (currentTouched[fieldName] && currentErrors[fieldName]) {
      return `${baseClass} border-red-300 focus:border-red-500 focus:ring-red-500`;
    }
    
    return baseClass;
  };

  return (
    <div className="space-y-8 text-sm">
      {/* ───────── Basic Info ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">Basic Information</h3>

        <div>
          <input
            placeholder="Client Name *"
            name="name"
            className={getFieldClassName('name')}
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
          />
          {currentTouched.name && currentErrors.name && (
            <p className="text-xs text-red-600 mt-1">⚠ {currentErrors.name}</p>
          )}
        </div>

        <input
          placeholder="Contact Name"
          className="input"
          value={form.contact_name}
          onChange={(e) => handleChange('contact_name', e.target.value)}
        />

        <div>
          <input
            placeholder="Phone"
            name="phone"
            type="tel"
            className={getFieldClassName('phone')}
            value={form.phone}
            onChange={(e) => {
              // Only allow numbers, spaces, +, -, (, )
              const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
              handleChange('phone', value);
            }}
            onBlur={() => handleBlur('phone')}
            onKeyPress={(e) => {
              // Prevent non-numeric characters (except +, -, space, parentheses)
              if (!/[0-9+\-\s()]/.test(e.key)) {
                e.preventDefault();
              }
            }}
          />
          {currentTouched.phone && currentErrors.phone ? (
            <p className="text-xs text-red-600 mt-1">⚠ {currentErrors.phone}</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Numbers only</p>
          )}
        </div>

        <div>
          <input
            placeholder="Email"
            name="email"
            type="email"
            className={getFieldClassName('email')}
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
          />
          {currentTouched.email && currentErrors.email && (
            <p className="text-xs text-red-600 mt-1">⚠ {currentErrors.email}</p>
          )}
        </div>
      </section>

      {/* ───────── Tax ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">Tax & Registration (Optional)</h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <input
              placeholder="GST IN"
              name="gstin"
              className={getFieldClassName('gstin')}
              value={form.gstin}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
                if (value.length <= 15) {
                  handleChange('gstin', value);
                }
              }}
              onBlur={() => handleBlur('gstin')}
              maxLength={15}
            />
            {currentTouched.gstin && currentErrors.gstin && (
              <p className="text-xs text-red-600 mt-1">⚠ {currentErrors.gstin}</p>
            )}
          </div>

          <input
            placeholder="PAN"
            className="input"
            value={form.pan}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
              if (value.length <= 10) {
                handleChange('pan', value);
              }
            }}
            maxLength={10}
          />

          <input
            placeholder="TIN"
            className="input"
            value={form.tin}
            onChange={(e) => handleChange('tin', e.target.value)}
          />

          <input
            placeholder="VAT No"
            className="input"
            value={form.vat_no}
            onChange={(e) => handleChange('vat_no', e.target.value)}
          />
        </div>

        <p className="text-xs text-[var(--color-text-muted)]">
          Required only if you issue tax invoices
        </p>
      </section>

      {/* ───────── Address ───────── */}
      <section className="space-y-4">
        <h3 className="section-title">Billing Address</h3>

        <textarea
          placeholder="Billing Address"
          rows={3}
          className="input resize-none"
          value={form.billing_address}
          onChange={(e) => handleChange('billing_address', e.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="City"
            className="input"
            value={form.city}
            onChange={(e) => handleChange('city', e.target.value)}
          />

          <input
            placeholder="State"
            className="input"
            value={form.state}
            onChange={(e) => handleChange('state', e.target.value)}
          />

          <div>
            <input
              placeholder="Pin Code"
              name="pincode"
              className={getFieldClassName('pincode')}
              value={form.pincode}
              onChange={(e) => {
                // Only allow numbers for pincode
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value.length <= 6) {
                  handleChange('pincode', value);
                }
              }}
              onBlur={() => handleBlur('pincode')}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={6}
            />
            {currentTouched.pincode && currentErrors.pincode && (
              <p className="text-xs text-red-600 mt-1">⚠ {currentErrors.pincode}</p>
            )}
          </div>

          <input
            placeholder="Country"
            className="input"
            value={form.country}
            onChange={(e) => handleChange('country', e.target.value)}
          />
        </div>
      </section>

      {/* ───────── Additional ───────── */}
      <section className="space-y-4 pb-6">
        <h3 className="section-title">Additional Details</h3>

        <textarea
          placeholder="Private Client Details"
          rows={2}
          className="input resize-none"
          value={form.private_notes}
          onChange={(e) => handleChange('private_notes', e.target.value)}
        />

        <textarea
          placeholder="Other Client Details"
          rows={2}
          className="input resize-none"
          value={form.other_notes}
          onChange={(e) => handleChange('other_notes', e.target.value)}
        />
      </section>
    </div>
  );
}
